import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Eye, Check, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";
import TagInput from "@/components/admin/TagInput";
import RichTextEditor from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { title: "", text: "", body_html: "", category: "", date: "", image: null, artwork_slug: "", status: "draft", seo_title: "", seo_description: "", featured: false, tags: [] };

function textToHtml(t) {
  if (!t) return "";
  return t.split(/\n{2,}/).map((block) => `<p>${block.replace(/\n/g, "<br>")}</p>`).join("");
}
function htmlToText(html) {
  const d = document.createElement("div");
  d.innerHTML = html || "";
  d.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  d.querySelectorAll("p, div, h2, h3, h4, li").forEach((el) => el.append("\n"));
  return (d.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

export default function PoemForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [autoState, setAutoState] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const idRef = useRef(id || null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    api.get("/poem-categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/admin/artworks").then(({ data }) => setArtworks(data)).catch(() => {});
    api.get("/admin/poem-tags").then(({ data }) => setTagSuggestions(data)).catch(() => {});
    if (id) {
      api.get(`/admin/poems/${id}`).then(({ data }) => {
        setF({ ...EMPTY, ...data, body_html: data.body_html || textToHtml(data.text), date: data.date ? data.date.slice(0, 10) : "" });
      }).catch(() => toast.error("Nicht gefunden")).finally(() => setLoading(false));
    }
  }, [id]);

  // Autosave: debounced draft save while typing (skips published poems, needs a title)
  useEffect(() => {
    if (loading || saving) return;
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    if (!f.title.trim() || f.status === "published") return;
    const snapshot = f;
    const t = setTimeout(async () => {
      setAutoState("saving");
      const payload = { ...snapshot, status: "draft", artwork_slug: snapshot.artwork_slug || null, date: snapshot.date || null, text: htmlToText(snapshot.body_html) };
      try {
        if (idRef.current) {
          await api.put(`/admin/poems/${idRef.current}`, payload);
        } else {
          const { data } = await api.post("/admin/poems", payload);
          idRef.current = data.id;
          window.history.replaceState(null, "", `/admin/gedichte/${data.id}`);
        }
        setSavedAt(new Date());
        setAutoState("saved");
      } catch { setAutoState(""); }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, loading]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async (status) => {
    if (!f.title.trim()) { toast.error("Bitte einen Titel eingeben"); return; }
    setSaving(true);
    const payload = { ...f, status, artwork_slug: f.artwork_slug || null, date: f.date || null, text: htmlToText(f.body_html) };
    try {
      if (idRef.current) await api.put(`/admin/poems/${idRef.current}`, payload);
      else await api.post("/admin/poems", payload);
      toast.success(status === "published" ? "Veröffentlicht" : "Entwurf gespeichert");
      navigate("/admin/gedichte");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-[#9A86B4]">Laden…</p>;

  return (
    <div className="fade-in max-w-3xl">
      <button onClick={() => navigate("/admin/gedichte")} className="inline-flex items-center gap-1 text-sm text-[#6E5A82] hover:text-ink mb-6"><ChevronLeft className="w-4 h-4" /> Zurück</button>
      <h1 className="font-serif text-4xl font-light mb-8">{id ? "Gedicht bearbeiten" : "Neues Gedicht"}</h1>

      <div className="space-y-6 bg-white border border-[#E7D8C6] p-5 sm:p-8">
        <div>
          <Label className="mb-1.5 block">Titel *</Label>
          <Input data-testid="poem-title-input" value={f.title} onChange={set("title")} className="rounded-md h-12 font-serif text-xl" />
        </div>
        <div>
          <Label className="mb-1.5 block">Gedicht *</Label>
          <RichTextEditor value={f.body_html} onChange={(v) => setF({ ...f, body_html: v })} testId="poem-body-editor" />
          <p className="text-xs text-[#9A86B4] mt-1.5">Zeilenumbrüche und Strophen bleiben erhalten. Formatieren Sie einzelne Wörter fett/kursiv über die Leiste.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="mb-1.5 block">Kategorie</Label>
            <Select value={f.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="poem-category" className="rounded-md h-12"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">Datum</Label>
            <Input type="date" value={f.date} onChange={set("date")} className="rounded-md h-12" />
          </div>
        </div>

        <ImageUpload label="Bild (optional)" value={f.image} onChange={set("image")} />

        <div>
          <Label className="mb-1.5 block">Zugehöriges Kunstwerk (optional)</Label>
          <Select value={f.artwork_slug || "none"} onValueChange={(v) => setF({ ...f, artwork_slug: v === "none" ? "" : v })}>
            <SelectTrigger className="rounded-md h-12"><SelectValue placeholder="Keines" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Keines</SelectItem>
              {artworks.map((a) => <SelectItem key={a.id} value={a.slug}>{a.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <label className="flex items-center gap-3">
          <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="w-4 h-4" />
          <span className="text-sm">Auf Startseite hervorheben</span>
        </label>

        <div>
          <Label className="mb-1.5 block">Tags (Themen, Stichworte)</Label>
          <TagInput value={f.tags} onChange={(v) => setF({ ...f, tags: v })} suggestions={tagSuggestions} testId="poem-tags-input" />
        </div>

        <details className="border border-[#F5EBDB] p-4">
          <summary className="cursor-pointer text-sm font-medium">SEO (optional)</summary>
          <div className="mt-4 space-y-4">
            <div><Label className="mb-1.5 block">SEO-Titel</Label><Input value={f.seo_title} onChange={set("seo_title")} className="rounded-md h-12" /></div>
            <div><Label className="mb-1.5 block">SEO-Beschreibung</Label><Textarea rows={2} value={f.seo_description} onChange={set("seo_description")} className="rounded-md" /></div>
          </div>
        </details>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-6 sticky bottom-0 bg-[#FFF4D6] py-4">
        <Button onClick={() => save("draft")} disabled={saving} variant="outline" data-testid="poem-save-draft" className="rounded-md">Entwurf speichern</Button>
        <Button onClick={() => setPreview(true)} variant="ghost" className="rounded-md"><Eye className="w-4 h-4 mr-2" /> Vorschau</Button>
        <Button onClick={() => save("published")} disabled={saving} data-testid="poem-publish" className="bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md">{id ? "Aktualisieren & Veröffentlichen" : "Veröffentlichen"}</Button>
        <div className="text-xs text-[#6E5A82] ml-auto min-h-[20px] flex items-center gap-1.5" data-testid="autosave-status" aria-live="polite">
          {f.status === "published" ? (
            <span className="text-[#9A86B4]">Autospeichern für veröffentlichte Gedichte deaktiviert</span>
          ) : autoState === "saving" ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Entwurf wird gespeichert…</>
          ) : autoState === "saved" && savedAt ? (
            <><Check className="w-3.5 h-3.5 text-[#4E9A51]" /> Automatisch gespeichert · {savedAt.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}</>
          ) : null}
        </div>
      </div>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="bg-[#FFF9F2] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="sr-only">Vorschau</DialogTitle></DialogHeader>
          <div className="text-center py-4">
            {f.category && <div className="text-xs uppercase tracking-[0.25em] text-[#9A86B4] mb-3">{f.category}</div>}
            <h2 className="font-serif text-4xl font-light">{f.title || "Ohne Titel"}</h2>
            <div className="poem-body text-xl leading-[1.7] mt-8 text-left px-2" dangerouslySetInnerHTML={{ __html: f.body_html || "" }} />
            <div className="mt-8 pt-4 border-t border-[#E7D8C6] font-serif">Susanne Monika Gysin</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
